// Per-agent performance breakdown ported from the legacy Scorecards
// dashboard (components/charts/PerformanceIndicatorBar/
// AgentPerformanceChart): a ranked table with a pass-rate / average-
// score toggle, an inline performance indicator bar, and drill-down
// links into each agent's passing and failing calls. Kept distinct
// from the bar-chart `AgentPerformanceWidget` -- users asked for this
// detailed table (and its CSV export) back on the new dashboard.
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { startCase, isEmpty } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { ExternalCallIdsLink } from '@/views/QACopilot/components/ExternalCallIdsLink'
import { PerformanceIndicatorBar } from '@/components/charts/PerformanceIndicatorBar/PerformanceIndicatorBar'
import { CHANNEL_NAME_PLURAL_DISPLAY } from '@/utils/constants'

import { ExportCsvButton } from '../components/ExportCsvButton'
import { downloadCsvFile } from '../helpers/format'

// Shared with the legacy chart so a user's ranking preference carries
// across both surfaces.
const ORDER_BY_KEY = 'settings__agent_performance:order_by'
const ORDER_BY_OPTIONS = ['pass_rate', 'average_score']

export const AgentPerformanceTableWidget = () => {
  // Defaulted here (not at each map site): SET_DATA replaces the whole
  // reportingByUser object on fetch, so the store's initial [] doesn't
  // survive an API response that omits the key.
  const agents =
    useSelector((state) => state.qaCopilot.data.reportingByUser.aggregate_scores_by_user) ?? []
  const loading = useSelector((state) => state.qaCopilot.loading.reportingByUser)
  const appliedCallType = useSelector((state) => state.qaCopilot.data.appliedCallType)
  const channelLabel = CHANNEL_NAME_PLURAL_DISPLAY[appliedCallType]

  const [orderBy, setOrderBy] = useState('pass_rate')
  const percentToDisplayLabel = `${startCase(orderBy)} Indicator`

  useEffect(() => {
    const stored = localStorage.getItem(ORDER_BY_KEY)
    if (stored && ORDER_BY_OPTIONS.includes(stored) && stored !== orderBy) {
      setOrderBy(stored)
    }
  }, [])

  const handleChangeOrderBy = (value) => {
    setOrderBy(value)
    localStorage.setItem(ORDER_BY_KEY, value)
  }

  const columns = [
    { accessor: 'full_name', label: 'Agent', collapsing: true, noWrap: true },
    {
      accessor: 'percent_to_display',
      label: percentToDisplayLabel,
      headerAlignment: 'center',
      isSortable: false,
    },
    { accessor: 'pass_rate', label: 'Pass Rate', collapsing: true },
    { accessor: 'average_score', label: 'Average Score', collapsing: true },
    { accessor: 'passed_call_count', label: `Passing ${channelLabel}`, collapsing: true },
    { accessor: 'failed_call_count', label: `Failing ${channelLabel}`, collapsing: true },
  ]

  const rows = useMemo(
    () =>
      agents.map((agent) => ({
        ...agent,
        // AdvancedTable sorts the Agent column by `last_name` (see
        // AdvancedTable/helpers.js), so derive it or that sort is a
        // no-op. Ported from the legacy AgentPerformanceChart.
        last_name: (agent.full_name || '').split(' ').pop(),
        average_score: {
          as: `${parseFloat(agent.average_score)}%`,
          // Numeric so the column sorts numerically -- a string value
          // sorts lexicographically ("9" after "10").
          value: parseFloat(agent.average_score),
        },
        pass_rate: {
          as: `${parseInt(agent.passing_average, 10)}%`,
          value: parseFloat(agent.passing_average),
        },
        percent_to_display: {
          as: PerformanceIndicatorBar({
            percent:
              orderBy === 'pass_rate'
                ? parseFloat(agent.passing_average)
                : parseFloat(agent.average_score),
          }),
        },
        passed_call_count: {
          as: !isEmpty(agent.passed_calls) ? (
            <ExternalCallIdsLink
              callIds={agent.passed_calls}
              label={agent.passed_calls.length}
              className="muted-text small-text"
              channelType={appliedCallType}
            />
          ) : (
            '0'
          ),
          value: agent.passed_calls?.length ?? 0,
        },
        failed_call_count: {
          as: !isEmpty(agent.failed_calls) ? (
            <ExternalCallIdsLink
              callIds={agent.failed_calls}
              label={agent.failed_calls.length}
              className="muted-text small-text"
              channelType={appliedCallType}
            />
          ) : (
            '0'
          ),
          value: agent.failed_calls?.length ?? 0,
        },
      })),
    [agents, orderBy, appliedCallType]
  )

  const handleDownload = () => {
    if (isEmpty(agents)) {
      return
    }
    const header = [
      'Agent',
      'Pass Rate',
      'Average Score',
      `Passing ${channelLabel}`,
      `Failing ${channelLabel}`,
    ]
    const csvRows = agents.map((agent) => [
      agent.full_name,
      parseInt(agent.passing_average, 10),
      parseFloat(agent.average_score),
      agent.passed_calls?.length ?? 0,
      agent.failed_calls?.length ?? 0,
    ])
    downloadCsvFile('agent-performance-detailed.csv', header, csvRows)
  }

  const orderItems = ORDER_BY_OPTIONS.map((option) => ({
    accessor: option,
    label: startCase(option),
    active: orderBy === option,
    onClick: () => handleChangeOrderBy(option),
    dataTestId: `agent-performance-order-${option}`,
  }))

  return (
    <Card
      title="Agent Performance"
      info="Each agent’s pass rate, average score, and passing/failing call counts for the current filters. Sort any column or export to CSV."
    >
      <div className="agent-performance-toolbar">
        <div className="agent-performance-rank-by">
          <span className="agent-performance-rank-by-label">Rank by</span>
          <SegmentedControl items={orderItems} small />
        </div>
        <ExportCsvButton
          onClick={handleDownload}
          disabled={isEmpty(agents)}
          label="Download CSV"
          tooltip="Download all agents as CSV"
          data-testid="agent-performance-download"
        />
      </div>
      <AdvancedTable
        rows={rows}
        columns={columns}
        loading={loading}
        defaultOrder="desc"
        defaultOrderBy={orderBy}
        testid="agent-performance-detailed-table"
        index="user_id"
        pagination
        wrapColumnContent={false}
      />
    </Card>
  )
}
